import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { DialogueIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button } from '@treams/treams-ui/common/button/Button';
import { Callout } from '@treams/treams-ui/common/Callout';
import { Empty } from '@treams/treams-ui/common/Empty';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { Pill } from '@treams/treams-ui/common/Pill';
import { Heading3, Paragraph, Subheading } from '@treams/treams-ui/common/Text';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { GrowthPathData } from '../GrowthPathConverter';
import { useGrowthPathJobCompetencyFilter } from '../hooks/useGrowthPathFilter';
import { GrowthPathJobCompetency } from '../types';
import GrowthPathCompetencyLevel from './GrowthPathCompetencyLevel';
import GrowthPathReceivedFeedback from './GrowthPathReceivedFeedback';

const CompetencyContainer = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: ${tokens.gap.group}px;
	padding: ${tokens.padding.section}px 0;
`;

const Levels = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	border-bottom: 1px solid ${tokens.border.subtle};
`;

export type GrowthPathReceivedFeedbackList = {
	scoresByJobCompetencyId: Record<string, graphql.CompetencyFeedbackScore[] | null>;
};

type Props = {
	data: GrowthPathData;
	labelID: string | null;
	receivedFeedback?: GrowthPathReceivedFeedbackList;
};

const GrowthPathCompetenciesList: FC<Props> = ({ data, labelID, receivedFeedback }) => {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const location = useLocation();
	const { jobCompetencies } = data;
	const showViewScoresButton = useFeature(graphql.FeatureName.CompetencyDetailScreen);

	const minLevels = new Map<string, number>();

	const filteredJobCompetencies = useGrowthPathJobCompetencyFilter(labelID, jobCompetencies) ?? [];
	for (const competency of filteredJobCompetencies) {
		minLevels.set(
			competency.competency.id,
			Math.max(competency.competencyLevel, minLevels.get(competency.id) ?? 0),
		);
	}

	const getAppraisal = (jobCompetency: GrowthPathJobCompetency) => {
		return jobCompetency.appraisal;
	};

	const getCompetencyLink = (jobCompetency: GrowthPathJobCompetency) => {
		const base = `${location.pathname}/competency/${jobCompetency.competency.id}`;
		return jobCompetency.jobTitleID ? `${base}?jobTitleID=${jobCompetency.jobTitleID}` : base;
	};

	return (
		<>
			{filteredJobCompetencies!.map((jobCompetency, i) => {
				const jobDescription = jobCompetency.description
					? localize(jobCompetency.description).text
					: undefined;
				const description = QuillText.isEmpty(jobDescription)
					? localize(jobCompetency.competency.description!).text
					: jobDescription;
				const hasDescription = !QuillText.isEmpty(description);

				return (
					<CompetencyContainer key={i}>
						<FlexGap flexDirection="column" gap={tokens.gap.inline}>
							<Subheading mb={0}>{localize(jobCompetency.competency.title!).text}</Subheading>
							{hasDescription && (
								<Paragraph small mb={0}>
									<QuillText value={description} />
								</Paragraph>
							)}
						</FlexGap>

						{(showViewScoresButton || !getAppraisal(jobCompetency)) && (
							<FlexGap gap={tokens.gap.list} alignItems="center" flexWrap="wrap">
								{!getAppraisal(jobCompetency) && (
									<Pill flexShrink={0}>{t('growthPath.competency.noScore')}</Pill>
								)}
								{showViewScoresButton && (
									<Button
										title={t('growthPath.competency.viewScores')}
										type="secondary"
										small
										link={getCompetencyLink(jobCompetency)}
									/>
								)}
							</FlexGap>
						)}

						<Levels>
							{jobCompetency.competency.levels.map((level, i) => (
								<GrowthPathCompetencyLevel
									key={i}
									index={i}
									appraisal={getAppraisal(jobCompetency)}
									level={level}
									min={minLevels.get(jobCompetency.competency.id!) ?? 0}
								/>
							))}
						</Levels>

						{receivedFeedback && (
							<ReceivedFeedbackSection
								competencyID={jobCompetency.competency.id}
								scores={
									jobCompetency.id in receivedFeedback.scoresByJobCompetencyId
										? receivedFeedback.scoresByJobCompetencyId[jobCompetency.id]
										: []
								}
								levels={jobCompetency.competency.levels as graphql.CompetencyLevelOption[]}
							/>
						)}
					</CompetencyContainer>
				);
			})}
		</>
	);
};

function ReceivedFeedbackSection(props: {
	competencyID: string;
	scores: graphql.CompetencyFeedbackScore[] | null;
	levels: graphql.CompetencyLevelOption[];
}) {
	const { t } = useTranslation();

	if (props.scores === null) {
		return <Callout color="warning">{t('growthPath.competency.feedback.unavailable')}</Callout>;
	}

	if (props.scores.length === 0) {
		return (
			<Empty
				icon={<DialogueIcon />}
				title={t('growthPath.competency.feedback.empty.title')}
				description={t('growthPath.competency.feedback.empty.description')}
			/>
		);
	}

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.list}>
			<Heading3 mb={0}>{t('growthPath.competency.feedback.title')}</Heading3>
			{props.scores.map((feedbackScore, index) => (
				<GrowthPathReceivedFeedback
					key={`${feedbackScore.feedbackResponse.id}-${index}`}
					competencyID={props.competencyID}
					feedbackScore={feedbackScore}
					levels={props.levels}
				/>
			))}
		</FlexGap>
	);
}

export default GrowthPathCompetenciesList;
